// @wc-ignore-file

import type { Project, Workspace } from "@shared/types/supabase.types";
// (Route paths and search values are data, not copy.)
import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

import { isSuperuser } from "@/features/superuser/is-superuser";
import { INCOGNITO_ACCESS } from "@/features/workspaces/workspace-access";
import type { WorkspaceAccess } from "@/features/workspaces/workspace-access";
import { incognitoQueries } from "./api/incognito-keys";
import {
  incognitoFallbackLink,
  incognitoWorkspaceProjectsLink,
  isIncognitoPathAllowed,
} from "./incognito-route";

type IncognitoGuardInput = {
  slug: string;
  pathname: string;
  email: string | null | undefined;
  queryClient: QueryClient;
};

/**
 * CR-116 D-B / D-C — the `/workspaces/$slug?incognito=support` load path.
 *
 * Order matters and each step fails closed:
 * 1. non-superuser → `/workspaces`, before any privileged RPC is called (the
 *    RPC would raise 42501 anyway — this keeps the attempt off the wire);
 * 2. a child route outside the allowlist → the incognito home (or, for a
 *    project page, the incognito project root — CR-165), before any child
 *    `beforeLoad` runs and before an audit row is written;
 * 3. the admin-only RPC; any failure → `/superuser/workspaces`.
 *
 * Never writes ACTIVE_WORKSPACE_SLUG_KEY or the recent-projects list.
 */
export async function loadIncognitoWorkspace({
  slug,
  pathname,
  email,
  queryClient,
}: IncognitoGuardInput): Promise<{ workspace: Workspace; workspaceAccess: WorkspaceAccess }> {
  if (!isSuperuser(email)) {
    throw redirect({ to: "/workspaces" });
  }

  if (!isIncognitoPathAllowed(pathname, slug)) {
    throw redirect(incognitoFallbackLink(pathname, slug));
  }

  let workspace: Workspace;
  try {
    workspace = await queryClient.ensureQueryData(incognitoQueries.workspaceBySlug(slug));
  } catch {
    throw redirect({ to: "/superuser/workspaces" });
  }

  return { workspace, workspaceAccess: INCOGNITO_ACCESS };
}

/**
 * CR-165 — the project layout's incognito load path. The workspace guard has
 * already checked the superuser gate and the allowlist for this pathname; this
 * reads the project through the admin-only RPC only. Never calls
 * `containerQueries.projectBySlugs` (a member view: PGRST116 for a non-member)
 * and never writes the recent-projects list or ACTIVE_PROJECT_SLUG_KEY. A
 * failure lands on the incognito projects list.
 */
export async function loadIncognitoProject({
  workspaceSlug,
  projectSlug,
  queryClient,
}: {
  workspaceSlug: string;
  projectSlug: string;
  queryClient: QueryClient;
}): Promise<{ project: Project }> {
  try {
    const project = await queryClient.ensureQueryData(
      incognitoQueries.projectBySlugs(workspaceSlug, projectSlug),
    );
    return { project };
  } catch {
    throw redirect(incognitoWorkspaceProjectsLink(workspaceSlug));
  }
}
